import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { device, useBreakpoints } from "../lib/breakpoints";
import { useConnectStatus } from "../hooks/useConnectStatus";
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  message,
  Spin,
  Alert,
  Divider,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Skeleton,
} from "antd";
import {
  HomeOutlined,
  UserOutlined,
  DollarOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  BankOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import {
  getRentPayment,
  updateRentPayment,
  subscribeRentPayment,
} from "../lib/firebase/paymentModel";
import unitModel from "../lib/firebase/unitModel";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import { getTenant } from "../lib/firebase/tenantModel";
import SettingsModel from "../lib/firebase/settingsModel";
import moment from "moment";
import {
  payRentPaymentCard,
  createRentPaymentIntentACH,
  getRentPaymentStatus,
  checkWebhookEndpoint,
} from "../lib/payments/stripe";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { AuthContext } from "../context/AuthProvider";
import { Spacer } from "../styles/CommonElements";
import Logo from "../components/general/Logo";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

/** Validates email structure (e.g. for receipt email before enabling Continue to bank). */
const isValidEmailStructure = (str) => {
  if (!str || typeof str !== "string") return false;
  const trimmed = str.trim();
  if (!trimmed) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

// Initialize Stripe
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

/** ACH via Stripe Payment Element (Instant Verification / Financial Connections). Uses Link or Connect modal, no manual routing/account. */
const BankPaymentForm = ({
  clientSecret,
  returnUrl,
  rentPaymentId,
  onSuccess,
  onError,
  onBack,
  /** Called when user submits the form (before Stripe confirm) so parent can show "Processing payment" UI. */
  onSubmitting,
  /** Email to prefill in the Payment Element (from "Email for Receipt" on previous screen). Passed as PaymentElement options.defaultValues. */
  prefillEmail,
  /** Total payment amount (rent + transaction fee) for the button label, e.g. "1,250.00". */
  totalAmountLabel,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;
    setError(null);
    onSubmitting?.();
    setSubmitting(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        const msg = submitError?.message || "Please complete the form.";
        setError(msg);
        onError?.({ message: msg });
        setSubmitting(false);
        return;
      }
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: returnUrl },
        redirect: "if_required",
      });
      if (confirmError) {
        const msg = confirmError.message || "Payment could not be completed.";
        setError(msg);
        onError?.({ message: msg });
        setSubmitting(false);
        return;
      }
      // Stripe accepted the payment; backend may not have updated yet, so we always show success
      let status = "processing";
      try {
        const statusRes = await getRentPaymentStatus(rentPaymentId);
        const resolved = statusRes?.data?.status ?? statusRes?.status;
        if (
          resolved === "succeeded" ||
          resolved === "paid" ||
          resolved === "processing"
        ) {
          status = resolved;
        }
      } catch (_) {
        // Backend not updated yet; treat as processing so user sees confirmation
      }
      onSuccess?.({ status, data: null });
    } catch (err) {
      const msg = err.message || "Something went wrong.";
      setError(msg);
      onError?.(err);
    } finally {
      setSubmitting(false);
    }
  };

  const paymentElementOptions =
    prefillEmail && prefillEmail.trim()
      ? {
          defaultValues: {
            billingDetails: {
              email: prefillEmail.trim(),
            },
          },
        }
      : undefined;

  const handlePaymentElementChange = (event) => {
    if (event?.complete) setError(null);
  };

  return (
    <BankFormWrapper onSubmit={handleSubmit}>
      <PaymentElementWrapper>
        <PaymentElement
          options={paymentElementOptions}
          onChange={handlePaymentElementChange}
        />
      </PaymentElementWrapper>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
      <BankFormActions>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          size="large"
          className="bank-pay-btn"
        >
          {totalAmountLabel
            ? `Pay $${totalAmountLabel} with bank account`
            : "Pay with bank account"}
        </Button>
        <BackButton
          type="button"
          onClick={onBack}
          disabled={submitting}
          size="large"
          className="bank-back-btn"
        >
          Back
        </BackButton>
      </BankFormActions>
    </BankFormWrapper>
  );
};

/** Manual ACH entry (routing + account number) for tenants whose bank is not supported by Stripe instant verification. */
const ManualBankForm = ({
  rentPaymentId,
  returnUrl,
  totalAmountLabel,
  prefillName,
  onSuccess,
  onError,
  onBack,
  onSubmitting,
}) => {
  const stripe = useStripe();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!stripe) return;
    try {
      await form.validateFields();
    } catch {
      return;
    }
    const {
      account_holder_name,
      routing_number,
      account_number,
      account_holder_type,
    } = form.getFieldsValue();
    const name =
      (account_holder_name && String(account_holder_name).trim()) || "";
    if (!name) {
      setError("Please enter the account holder name.");
      return;
    }
    setError(null);
    onSubmitting?.();
    setSubmitting(true);
    try {
      const { paymentMethod, error: pmError } =
        await stripe.createPaymentMethod({
          type: "us_bank_account",
          us_bank_account: {
            routing_number: String(routing_number).replace(/\D/g, ""),
            account_number: String(account_number).replace(/\D/g, ""),
            account_holder_type: account_holder_type || "individual",
          },
          billing_details: {
            name: name,
          },
        });
      if (pmError) {
        const msg =
          pmError?.message ||
          "Invalid bank details. Please check and try again.";
        setError(msg);
        onError?.({ message: msg });
        setSubmitting(false);
        return;
      }
      if (!paymentMethod?.id) {
        throw new Error("Could not set up bank account.");
      }
      const res = await createRentPaymentIntentACH({
        rentPaymentId,
        bankAccountToken: paymentMethod.id,
      });
      if (!res?.client_secret) {
        throw new Error(res?.error || "Could not start payment.");
      }
      const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
        clientSecret: res.client_secret,
        confirmParams: { return_url: returnUrl },
        redirect: "if_required",
      });
      if (confirmErr) {
        const msg = confirmErr?.message || "Payment could not be completed.";
        setError(msg);
        onError?.(confirmErr);
        setSubmitting(false);
        return;
      }
      if (paymentIntent?.next_action?.type === "verify_with_microdeposits") {
        const url =
          paymentIntent.next_action?.verify_with_microdeposits
            ?.hosted_verification_url;
        if (url) {
          window.location.href = url;
          return;
        }
      }
      let status = paymentIntent?.status ?? "processing";
      if (status === "succeeded" || status === "requires_capture")
        status = "succeeded";
      onSuccess?.({ status, data: null });
    } catch (e) {
      const msg = e?.message || "Something went wrong.";
      setError(msg);
      onError?.(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BankFormWrapper as="div">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ account_holder_name: prefillName || "" }}
      >
        <Form.Item
          name="account_holder_name"
          label="Account holder name"
          rules={[
            { required: true, message: "Enter the name on the bank account" },
          ]}
        >
          <Input placeholder="Name on bank account" />
        </Form.Item>
        <Form.Item
          name="routing_number"
          label="Routing number"
          rules={[
            { required: true, message: "Enter your routing number" },
            { pattern: /^\d{9}$/, message: "Routing number must be 9 digits" },
          ]}
          normalize={(v) => (v ? String(v).replace(/\D/g, "").slice(0, 9) : v)}
        >
          <Input placeholder="9-digit routing number" maxLength={9} />
        </Form.Item>
        <Form.Item
          name="account_number"
          label="Account number"
          rules={[
            { required: true, message: "Enter your account number" },
            {
              pattern: /^\d{4,17}$/,
              message: "Account number must be 4 to 17 digits",
            },
          ]}
          normalize={(v) => (v ? String(v).replace(/\D/g, "").slice(0, 17) : v)}
        >
          <Input
            placeholder="Account number"
            type="password"
            autoComplete="off"
          />
        </Form.Item>
        <Form.Item
          name="account_holder_type"
          label="Account type"
          initialValue="individual"
          rules={[{ required: true }]}
        >
          <Select>
            <Option value="individual">Personal</Option>
            <Option value="company">Business</Option>
          </Select>
        </Form.Item>
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <BankFormActions>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            size="large"
            className="bank-pay-btn"
          >
            {totalAmountLabel
              ? `Pay $${totalAmountLabel}`
              : "Pay with bank account"}
          </Button>
          <BackButton
            type="button"
            onClick={onBack}
            disabled={submitting}
            size="large"
            className="bank-back-btn"
          >
            Back
          </BackButton>
        </BankFormActions>
      </Form>
    </BankFormWrapper>
  );
};

// Inner component that uses Stripe hooks
const TenantRentPaymentInner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoints();
  const { user } = useContext(AuthContext);
  const stripe = useStripe();
  const elements = useElements();
  const [form] = Form.useForm();
  const emailForReceipt = Form.useWatch("email", form);
  const receiptEmailTrimmed =
    emailForReceipt && String(emailForReceipt).trim()
      ? String(emailForReceipt).trim()
      : "";
  const isValidReceiptEmail = isValidEmailStructure(receiptEmailTrimmed);

  // Build return URL for PaymentIntent confirmation
  const APP_URL = window.location.origin;
  const returnUrl = `${APP_URL}/payments/return?rentPaymentId=${id}`;

  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("bank");
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  /** When set, we show Stripe Payment Element for ACH (Instant Verification) instead of manual bank fields */
  const [achClientSecret, setAchClientSecret] = useState(null);
  /** When 'manual', show manual ACH form (routing/account) for banks not supported by Stripe instant verification */
  const [achEntryMode, setAchEntryMode] = useState(null);
  /** Email to prefill in Stripe Payment Element (set when user clicks Continue to bank account) */
  const [emailForStripePrefill, setEmailForStripePrefill] = useState("");
  // Payment status tracking for ACH verification
  const [paymentStatus, setPaymentStatus] = useState(null); // 'requires_verification', 'processing', 'succeeded', 'failed'
  const [verificationUrl, setVerificationUrl] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  /** True while waiting for payment submit to complete (card or ACH); show "Processing your payment" step. */
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  // Polling state for async ACH payments
  const [pollingInterval, setPollingInterval] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);
  const MAX_POLLING_ATTEMPTS = 12; // 12 attempts * 5 seconds = 60 seconds max
  // Auto-detect test mode from Stripe publishable key
  const stripePublishableKey =
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
  const inferredTestMode = stripePublishableKey.startsWith("pk_test_");
  const [testMode, setTestMode] = useState(inferredTestMode);

  // API URL for backend calls
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

  // Phone number formatting utility
  const formatPhoneNumber = (value) => {
    if (!value) return "";

    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, "");

    // Don't format if empty or too short
    if (phoneNumber.length === 0) return "";
    if (phoneNumber.length < 4) return phoneNumber;

    // Format based on length
    if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length < 11) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6)}`;
    } else {
      // Handle longer numbers (with country code)
      return `+${phoneNumber.slice(
        0,
        phoneNumber.length - 10
      )} (${phoneNumber.slice(-10, -7)}) ${phoneNumber.slice(
        -7,
        -4
      )}-${phoneNumber.slice(-4)}`;
    }
  };

  // Fetch payment details (refetch used after successful payment to show updated status)
  const {
    data: payment,
    isLoading: paymentLoading,
    error: paymentError,
    refetch: refetchPayment,
  } = useQuery({
    queryKey: ["payment", id],
    queryFn: async () => {
      console.log("Fetching payment with ID:", id);
      const result = await getRentPayment(id);
      console.log("Payment fetch result:", result);
      if (result.success) {
        console.log("Payment data details:", {
          id: result.data.id,
          amount: result.data.amount,
          status: result.data.status,
          dueDate: result.data.dueDate,
          tenantId: result.data.tenantId,
          unitId: result.data.unitId,
          ownerId: result.data.ownerId,
        });
      }
      return result.success ? result.data : null;
    },
    enabled: !!id,
  });

  // Extract tenantId from payment to avoid TDZ issues
  const tenantId = payment?.tenantId;

  // Fetch tenant details (needed for unitId fallback)
  const { data: tenant, isLoading: tenantLoading } = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: async () => {
      try {
        console.log("Fetching tenant with ID:", tenantId);
        const result = await getTenant(tenantId);
        console.log("Tenant fetch result:", result);
        if (result) {
          console.log("Tenant data details:", {
            id: result.id,
            firstName: result.firstName,
            lastName: result.lastName,
            email: result.email,
            phone: result.phone,
            unitId: result.unitId, // Log unitId from tenant
          });
        }
        return result;
      } catch (error) {
        console.error("Error fetching tenant:", error);
        return null;
      }
    },
    enabled: !!tenantId,
  });

  // Fetch unit details - use unitId from payment, or fallback to tenant's unitId
  // Extract both unitIds separately to avoid TDZ issues
  const paymentUnitId = payment?.unitId;
  const tenantUnitId = tenant?.unitId;
  const { data: unit, isLoading: unitLoading } = useQuery({
    queryKey: ["unit", paymentUnitId, tenantUnitId],
    queryFn: async () => {
      // Get unitId from payment first, then tenant as fallback
      const unitId = payment?.unitId || tenant?.unitId;
      console.log(
        "Fetching unit with ID:",
        unitId,
        "(from payment:",
        payment?.unitId,
        ", from tenant:",
        tenant?.unitId,
        ")"
      );

      if (!unitId) {
        console.warn("No unitId available from payment or tenant");
        return null;
      }

      const result = await unitModel.getOneById(unitId);
      console.log("Unit fetch result:", result);
      if (result) {
        console.log("Unit data details:", {
          id: result.id,
          address: result.address,
          unitNumber: result.unitNumber,
          rent: result.rent,
          ownerId: result.ownerId,
        });
      }
      return result;
    },
    enabled: !!(payment && (payment.unitId || tenant?.unitId)),
  });

  // Fetch landlord settings to check payment setup
  const { data: landlordSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ["landlord-settings", payment?.ownerId],
    queryFn: async () => {
      try {
        const result = await SettingsModel.getByUserId(payment?.ownerId);
        return result;
      } catch (error) {
        console.error("Error fetching landlord settings:", error);
        return null;
      }
    },
    enabled: !!payment?.ownerId,
  });

  // Check Connect status for real-time payment capability
  const {
    canAcceptPayments,
    showRentPaymentForm,
    alertMessage,
    alertType,
    loading: connectStatusLoading,
    connectStatus,
  } = useConnectStatus(payment?.ownerId);

  // Check for payment error or not found FIRST, before other loading checks
  // This ensures we show the error page immediately when payment is missing
  const isPaymentNotFound = !paymentLoading && (paymentError || !payment);

  // Ensure we have payment data (with ownerId) before checking connect status
  // Also ensure connect status has been determined (not null) before showing content
  // This prevents showing alerts before we have all the data
  // Only check other loading states if payment exists
  const isLoading =
    !isPaymentNotFound &&
    (paymentLoading ||
      unitLoading ||
      tenantLoading ||
      settingsLoading ||
      connectStatusLoading ||
      !payment?.ownerId ||
      !connectStatus);
  const isAlreadyPaid =
    payment?.status === "paid" ||
    payment?.status === "succeeded" ||
    payment?.status === "processing" ||
    payment?.status === "pending_verification" ||
    payment?.status === "PENDING_VERIFICATION";

  // Check if payment requires verification on page load
  useEffect(() => {
    if (
      payment?.status === "pending_verification" ||
      payment?.status === "PENDING_VERIFICATION"
    ) {
      setPaymentStatus("requires_verification");
      if (payment?.verificationUrl) {
        setVerificationUrl(payment.verificationUrl);
      }
      if (payment?.stripePaymentIntentId) {
        setPaymentIntentId(payment.stripePaymentIntentId);
      }
    } else if (payment?.status === "processing") {
      setPaymentStatus("processing");
      if (payment?.stripePaymentIntentId) {
        setPaymentIntentId(payment.stripePaymentIntentId);
      }
    }
  }, [payment]);

  // When payment is already paid, clear bank flow so user cannot attempt to pay again
  useEffect(() => {
    if (isAlreadyPaid && achClientSecret) {
      setAchClientSecret(null);
    }
  }, [isAlreadyPaid, achClientSecret]);

  // After successful payment, refresh after a short delay so the page shows updated status
  useEffect(() => {
    if (paymentStatus !== "succeeded") return;
    const t = setTimeout(() => {
      refetchPayment();
      setPaymentStatus(null);
    }, 2500);
    return () => clearTimeout(t);
  }, [paymentStatus, refetchPayment]);

  // Check webhook endpoint availability (development/debugging)
  useEffect(() => {
    const checkWebhook = async () => {
      try {
        const webhookStatus = await checkWebhookEndpoint();
        if (!webhookStatus.exists) {
          console.warn("[TenantRentPayment] ⚠️ Webhook endpoint not found:", {
            endpoint: "/api/payments/stripe/webhook",
            status: webhookStatus,
            message:
              "Backend webhook handler not implemented. Payment status updates will rely on polling only.",
            action:
              "See BACKEND_WEBHOOK_IMPLEMENTATION_PROMPT.md for implementation requirements",
          });
        } else if (!webhookStatus.accessible) {
          console.warn(
            "[TenantRentPayment] ⚠️ Webhook endpoint exists but not accessible:",
            {
              endpoint: "/api/payments/stripe/webhook",
              status: webhookStatus,
              message:
                "Webhook endpoint may require authentication or have configuration issues.",
            }
          );
        } else {
          console.log("[TenantRentPayment] ✅ Webhook endpoint is available:", {
            endpoint: "/api/payments/stripe/webhook",
            status: webhookStatus.status,
          });
        }
      } catch (error) {
        // Non-critical - don't block payment flow
        console.debug(
          "[TenantRentPayment] Could not check webhook endpoint:",
          error.message
        );
      }
    };

    // Only check in development or if explicitly enabled
    if (
      process.env.NODE_ENV === "development" ||
      process.env.REACT_APP_CHECK_WEBHOOK === "true"
    ) {
      checkWebhook();
    }
  }, []);

  // Live subscription to reflect webhook updates after initiating payment
  useEffect(() => {
    if (!id) return;
    const unsub = subscribeRentPayment(id, (doc) => {
      if (!doc) return;
      if (doc.status === "paid") {
        message.success("Payment completed");
      }
    });
    return () => unsub && unsub();
  }, [id]);

  // Fetch fee breakdown when payment method changes or payment loads
  useEffect(() => {
    const fetchFeeBreakdown = async () => {
      if (!id || !payment || !canAcceptPayments || isAlreadyPaid) {
        console.log("[Fee Breakdown] Skipping fetch - conditions not met:", {
          hasId: !!id,
          hasPayment: !!payment,
          canAcceptPayments,
          isAlreadyPaid,
        });
        setFeeBreakdown(null);
        return;
      }

      setFeeLoading(true);
      try {
        // Map payment method: "card" -> "card", "bank" -> "ach"
        const methodParam = paymentMethod === "bank" ? "ach" : "card";
        // API_URL already includes /api (e.g., "http://localhost:4000/api")
        const url = `${API_URL}/payments/${id}/fees?method=${methodParam}`;
        console.log("[Fee Breakdown] Fetching from:", url);

        // Get Firebase auth token for authentication
        const { firebaseAuth } = await import("../helpers/authentication");
        const user = firebaseAuth.currentUser;
        if (!user) {
          throw new Error("User not authenticated");
        }
        const authToken = await user.getIdToken();

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        });

        console.log(
          "[Fee Breakdown] Response status:",
          response.status,
          response.statusText
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch fee breakdown: ${response.status} ${response.statusText}`
          );
        }

        const result = await response.json();
        console.log("[Fee Breakdown] Response data:", result);

        if (result.success && result.data) {
          console.log("[Fee Breakdown] Setting fee breakdown:", result.data);
          setFeeBreakdown(result.data);
        } else {
          console.warn(
            "[Fee Breakdown] Response missing data or not successful:",
            result
          );
          setFeeBreakdown(null);
        }
      } catch (error) {
        console.error("[Fee Breakdown] Error fetching fee breakdown:", error);
        // Don't show error to user, just log it - payment can still proceed
        setFeeBreakdown(null);
      } finally {
        setFeeLoading(false);
      }
    };

    fetchFeeBreakdown();
  }, [id, payment, paymentMethod, canAcceptPayments, isAlreadyPaid, API_URL]);

  // Debug payment data
  console.log("Payment component state:", {
    payment,
    paymentLoading,
    paymentError,
    unit,
    unitLoading,
    tenant,
    landlordSettings,
    isLoading,
  });

  // Get unitId from payment or tenant (fallback)
  const effectiveUnitId = payment?.unitId || tenant?.unitId;
  console.log(
    "Unit query enabled:",
    !!effectiveUnitId,
    "Unit ID:",
    effectiveUnitId,
    "From payment:",
    payment?.unitId,
    "From tenant:",
    tenant?.unitId
  );

  // Debug fee breakdown state
  console.log("[Fee Breakdown] Current state:", {
    feeBreakdown,
    feeLoading,
    canAcceptPayments,
    isAlreadyPaid,
    paymentMethod,
    id,
  });

  // Adjusted amount due = base + sum(adjustments). Use for display when fees API isn't used.
  const getAdjustedAmountDue = (p) => {
    if (!p) return 0;
    const base = p.amount ?? p.amountDue ?? 0;
    const sum = (p.adjustments || []).reduce((s, a) => s + (a?.amount ?? 0), 0);
    return base + sum;
  };
  // Amount due to show: prefer fees API (adjusted rent), else computed adjusted total
  const displayAmountDue = payment
    ? feeBreakdown != null
      ? parseFloat(
          String(feeBreakdown.rentAmount?.dollars ?? "").replace(/,/g, "")
        ) || getAdjustedAmountDue(payment)
      : getAdjustedAmountDue(payment)
    : 0;

  // Total to charge (rent + transaction fee) for button labels. Use fee breakdown when available; else compute fee client-side.
  const displayTotalWithFee =
    feeBreakdown != null
      ? (() => {
          const totalStr = feeBreakdown.totalAmount?.dollars;
          if (totalStr != null && totalStr !== "") {
            const num = parseFloat(String(totalStr).replace(/,/g, ""));
            return Number.isFinite(num) ? num : null;
          }
          const rent = parseFloat(
            String(feeBreakdown.rentAmount?.dollars ?? "").replace(/,/g, "")
          );
          const fee = parseFloat(
            String(feeBreakdown.stripeFee?.dollars ?? "").replace(/,/g, "")
          );
          if (Number.isFinite(rent) && Number.isFinite(fee)) return rent + fee;
          return null;
        })()
      : null;

  // When fees API didn't return a breakdown, compute total (rent + fee) client-side so buttons always show full amount.
  const rentForFee = Number(displayAmountDue) || 0;
  const clientSideFee =
    paymentMethod === "bank"
      ? Math.min(rentForFee * 0.008, 5) // ACH: 0.8% cap $5
      : rentForFee * 0.029 + 0.3; // Card: 2.9% + $0.30
  const computedTotalWithFee =
    displayTotalWithFee != null
      ? displayTotalWithFee
      : rentForFee > 0
      ? rentForFee + clientSideFee
      : null;

  const displayTotalWithFeeFormatted =
    computedTotalWithFee != null
      ? computedTotalWithFee.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : displayAmountDue != null
      ? displayAmountDue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  const handlePayment = async (values) => {
    console.log(
      "[TenantRentPayment] handlePayment called with values:",
      values
    );
    if (!payment) {
      console.error("[TenantRentPayment] No payment data available");
      return;
    }
    if (isAlreadyPaid) {
      message.info("This payment is already marked as paid. No action taken.");
      return;
    }

    console.log("[TenantRentPayment] Starting payment processing...");
    setLoading(true);
    setIsSubmittingPayment(true);
    try {
      let result;
      let paymentMethodId = null; // Store payment method ID for status updates

      if (values.paymentMethod === "card") {
        // Use Stripe Elements to create Payment Method
        if (!stripe || !elements) {
          throw new Error("Stripe not loaded. Please refresh the page.");
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          throw new Error("Card element not found. Please refresh the page.");
        }

        console.log("[TenantRentPayment] Creating Stripe Payment Method...");

        // Create Payment Method from Stripe Elements card element
        const { error: pmError, paymentMethod: stripePaymentMethod } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              name: values.cardholderName,
              email: values.email,
            },
          });

        if (pmError) {
          throw new Error(
            pmError.message || "Failed to process card information"
          );
        }

        if (!stripePaymentMethod) {
          throw new Error("Failed to create payment method");
        }

        console.log(
          "[TenantRentPayment] Payment Method created:",
          stripePaymentMethod.id
        );
        paymentMethodId = stripePaymentMethod.id;
        console.log("[TenantRentPayment] Calling payRentPaymentCard with:", {
          rentPaymentId: id,
          paymentMethodId: paymentMethodId,
        });

        // Call backend API with Payment Method ID
        result = await payRentPaymentCard({
          rentPaymentId: id,
          paymentMethodId: paymentMethodId,
        });
      } else if (values.paymentMethod === "bank") {
        // ACH: Instant Verification; create PaymentIntent for Payment Element and show Stripe Link/Connect modal
        const paymentIntentResponse = await createRentPaymentIntentACH({
          rentPaymentId: id,
        });
        if (paymentIntentResponse?.client_secret) {
          setAchClientSecret(paymentIntentResponse.client_secret);
          setLoading(false);
          return;
        }
        throw new Error(
          paymentIntentResponse?.error || "Could not start bank payment"
        );
      } else {
        throw new Error("Invalid payment method selected");
      }

      console.log("[TenantRentPayment] Payment result:", result);

      // Extract status from result (backend may wrap in { success: true, data: {...} })
      // If result has a 'data' property, use that; otherwise use result directly
      const resultData = result?.data || result;
      const backendStatus = resultData?.status;
      const finalRentPaymentId =
        resultData?.rentPaymentId || result?.rentPaymentId || id;

      // Log PaymentIntent status and next_action for debugging
      console.log("[TenantRentPayment] Backend status:", backendStatus);
      console.log("[TenantRentPayment] Result data:", resultData);
      console.log(
        "[TenantRentPayment] Full PaymentIntent response:",
        JSON.stringify(result, null, 2)
      );

      // Helper function to start polling for async ACH payments
      const startPolling = (rentPaymentId) => {
        let attempts = 0;
        const pollInterval = setInterval(async () => {
          attempts++;
          setPollingCount(attempts);

          if (attempts >= MAX_POLLING_ATTEMPTS) {
            clearInterval(pollInterval);
            setPollingInterval(null);
            message.info(
              "We'll update you automatically when your payment status changes."
            );
            return;
          }

          try {
            const polledResponse = await getRentPaymentStatus(rentPaymentId);
            console.log(
              `[TenantRentPayment] Polling attempt ${attempts}:`,
              polledResponse
            );

            // Extract data from response (backend may wrap in { success: true, data: {...} })
            const polledStatus = polledResponse?.data || polledResponse;
            const polledBackendStatus = polledStatus?.status;

            // Check if status changed to succeeded or failed
            if (polledBackendStatus === "succeeded") {
              clearInterval(pollInterval);
              setPollingInterval(null);
              setPaymentStatus("succeeded");
              message.success("Payment processed successfully!");
            } else if (
              polledBackendStatus === "payment_failed" ||
              polledBackendStatus === "failed"
            ) {
              clearInterval(pollInterval);
              setPollingInterval(null);
              setPaymentStatus("failed");
              message.error(
                polledStatus?.error ||
                  polledStatus?.lastPaymentError ||
                  "Payment failed. Please try again."
              );
            } else if (
              polledBackendStatus === "pending_verification" ||
              polledBackendStatus === "created"
            ) {
              // Status changed to pending_verification - update UI
              setPaymentStatus("requires_verification");
              const polledVerificationUrl =
                polledStatus?.hostedVerificationUrl ||
                polledStatus?.hosted_verification_url;
              if (polledVerificationUrl) {
                setVerificationUrl(polledVerificationUrl);
              }
            }
          } catch (pollError) {
            console.error("[TenantRentPayment] Polling error:", pollError);
            // Continue polling on error
          }
        }, 5000); // Poll every 5 seconds

        setPollingInterval(pollInterval);
      };

      // Handle status: pending_verification (microdeposit verification required)
      if (
        backendStatus === "pending_verification" ||
        backendStatus === "created"
      ) {
        const hostedVerificationUrl =
          resultData?.hostedVerificationUrl ||
          resultData?.hosted_verification_url ||
          result?.hostedVerificationUrl;

        console.log(
          "[TenantRentPayment] Payment requires microdeposit verification"
        );
        console.log("[TenantRentPayment] Backend status:", backendStatus);
        console.log(
          "[TenantRentPayment] Hosted verification URL:",
          hostedVerificationUrl
        );

        // Store verification state
        setPaymentStatus("requires_verification");
        if (hostedVerificationUrl) {
          setVerificationUrl(hostedVerificationUrl);
        }
        setPaymentIntentId(
          resultData?.stripePaymentIntentId ||
            result?.paymentId ||
            result?.id ||
            resultData?.paymentIntentId
        );

        message.info("Bank account verification required");

        // Start polling to check when verification completes
        startPolling(finalRentPaymentId);
      } else if (backendStatus === "processing") {
        // Payment is processing (ACH can take a few business days)
        // DO NOT mark as paid - only show processing state
        setPaymentStatus("processing");
        setPaymentIntentId(
          resultData?.stripePaymentIntentId ||
            result?.paymentId ||
            result?.id ||
            resultData?.paymentIntentId
        );

        message.success(
          "Payment is processing. ACH payments can take a few business days."
        );

        // Start polling for status updates
        startPolling(finalRentPaymentId);
      } else if (backendStatus === "succeeded") {
        // Payment succeeded - ONLY mark as paid when backend explicitly says succeeded
        setPaymentStatus("succeeded");
        setPaymentIntentId(
          resultData?.stripePaymentIntentId ||
            result?.paymentId ||
            result?.id ||
            resultData?.paymentIntentId
        );

        message.success("Payment processed successfully!");
      } else if (
        backendStatus === "requires_payment_method" ||
        backendStatus === "payment_failed" ||
        backendStatus === "failed"
      ) {
        // Payment failed or requires a different payment method
        setPaymentStatus("failed");
        const errorMsg =
          resultData?.error?.message ||
          resultData?.error ||
          resultData?.lastPaymentError ||
          result?.error ||
          "Payment failed. Please try a different payment method or contact support.";
        console.error("[TenantRentPayment] Payment failed:", errorMsg);
        throw new Error(errorMsg);
      } else {
        // Unknown status - treat as error
        console.warn(
          "[TenantRentPayment] Unknown payment status:",
          backendStatus
        );
        console.warn("[TenantRentPayment] Full result object:", result);
        console.warn("[TenantRentPayment] Result data:", resultData);
        const errorMsg =
          resultData?.error ||
          resultData?.lastPaymentError ||
          result?.error ||
          `Payment status: ${
            backendStatus || "undefined"
          }. Please contact support if this persists.`;
        console.error(
          "[TenantRentPayment] Unknown payment status error:",
          errorMsg
        );
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error("Payment error:", error);
      message.error(error.message || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
      setIsSubmittingPayment(false);
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "paid":
      case "succeeded":
        return "success";
      case "overdue":
        return "error";
      case "pending":
        return "warning";
      case "pending_verification":
      case "PENDING_VERIFICATION":
      case "requires_verification":
        return "processing";
      case "created":
        return "warning"; // Intent created, payment not confirmed yet; same as "Due"
      case "processing":
        return "processing";
      case "failed":
      case "payment_failed":
      case "requires_payment_method":
        return "error";
      default:
        return "default";
    }
  };

  const getPaymentStatusText = (status) => {
    switch (status) {
      case "paid":
      case "succeeded":
        return "Paid";
      case "overdue":
        return "Overdue";
      case "pending":
        return "Due";
      case "pending_verification":
      case "PENDING_VERIFICATION":
      case "requires_verification":
        return "Verification Required";
      case "created":
        // Intent created but payment not yet confirmed (e.g. Element flow); show "Due" so we don't imply verification is required
        return "Due";
      case "processing":
        return "Processing";
      case "failed":
      case "payment_failed":
        return "Payment Failed";
      case "requires_payment_method":
        return "Payment Method Required";
      default:
        // Return human-readable version of status if it's a known status
        if (status) {
          // Convert snake_case to Title Case
          return status
            .split("_")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" ");
        }
        return "Unknown";
    }
  };

  // Get effective payment status (use local state if set, otherwise use payment.status)
  // Map local state values to backend status values for display
  const getEffectiveStatus = () => {
    if (paymentStatus) {
      // Map local state to backend status
      if (paymentStatus === "requires_verification") {
        return "pending_verification";
      }
      return paymentStatus;
    }
    return payment?.status;
  };
  const effectivePaymentStatus = getEffectiveStatus();

  // Helper function to safely format dates
  const formatDate = (dateValue) => {
    if (!dateValue) return "Not set";

    try {
      // Handle Firestore Timestamp objects
      if (dateValue && typeof dateValue.toDate === "function") {
        return moment(dateValue.toDate()).format("MMM DD, YYYY");
      }

      // Handle Date objects
      if (dateValue instanceof Date) {
        return moment(dateValue).format("MMM DD, YYYY");
      }

      // Handle timestamp numbers
      if (typeof dateValue === "number") {
        return moment(dateValue).format("MMM DD, YYYY");
      }

      // Handle string dates
      if (typeof dateValue === "string") {
        const parsed = moment(dateValue);
        if (parsed.isValid()) {
          return parsed.format("MMM DD, YYYY");
        }
      }

      return "Invalid date";
    } catch (error) {
      console.error("Error formatting date:", error, dateValue);
      return "Invalid date";
    }
  };

  // Show proper "Payment Not Found" page FIRST, before loading check
  // This ensures we show the error page immediately when payment is missing
  if (isPaymentNotFound) {
    console.log("Payment error or not found:", {
      paymentError,
      payment,
      paymentLoading,
      id,
    });
    return (
      <Container>
        <Header>
          <Logo size="large" displayMode="both" linkTo={null} />
          <Text type="secondary">Secure Rent Payment Portal</Text>
        </Header>

        <Content>
          <Row gutter={[24, 24]} justify="center">
            <Col xs={24} md={12}>
              <PaymentCard>
                <NotFoundContent>
                  <NotFoundIcon>
                    <SafetyOutlined
                      style={{ fontSize: "4rem", color: "#ff4d4f" }}
                    />
                  </NotFoundIcon>
                  <Title
                    level={2}
                    style={{ textAlign: "center", marginBottom: "1rem" }}
                  >
                    Payment Not Found
                  </Title>
                  <Paragraph
                    style={{
                      textAlign: "center",
                      fontSize: "1rem",
                      color: "#666",
                      marginBottom: "2rem",
                    }}
                  >
                    {paymentError
                      ? "We couldn't find the payment you're looking for. The payment link may be invalid, expired, or the payment may have already been processed."
                      : "The payment link you're looking for doesn't exist or has expired. Please check the link and try again, or contact your landlord for a new payment link."}
                  </Paragraph>

                  <NotFoundActions>
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => {
                        window.location.href = "/";
                      }}
                      block
                    >
                      Return to Home
                    </Button>
                    <Button
                      type="default"
                      size="large"
                      onClick={() => window.location.reload()}
                      block
                    >
                      Try Again
                    </Button>
                  </NotFoundActions>

                  <Divider style={{ margin: "2rem 0" }} />

                  <HelpSection>
                    <Title level={5} style={{ marginBottom: "1rem" }}>
                      Need Help?
                    </Title>
                    <Paragraph
                      style={{
                        fontSize: "0.9rem",
                        color: "#666",
                        marginBottom: 0,
                      }}
                    >
                      If you believe this is an error, please contact your
                      landlord or property manager. They can provide you with a
                      new payment link or help resolve any issues.
                    </Paragraph>
                  </HelpSection>
                </NotFoundContent>
              </PaymentCard>
            </Col>
          </Row>
        </Content>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <Header>
          <Logo size="large" displayMode="both" linkTo={null} />
          <Text type="secondary">Secure Rent Payment Portal</Text>
        </Header>

        <Content>
          <Row gutter={[24, 24]} justify="center">
            <Col xs={24} md={12}>
              <PaymentCard>
                <Skeleton
                  active
                  paragraph={{ rows: 0 }}
                  title={{ width: 150 }}
                  style={{ marginBottom: "1.5rem" }}
                />

                {/* Payment Summary Skeleton */}
                <PaymentSummary>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Skeleton
                        active
                        paragraph={{ rows: 0 }}
                        title={{ width: 100 }}
                      />
                      <div style={{ marginTop: "8px" }}>
                        <Skeleton
                          active
                          paragraph={{ rows: 0 }}
                          title={{ width: 80 }}
                          style={{ fontSize: "1.5rem" }}
                        />
                      </div>
                    </Col>
                    <Col span={12}>
                      <Skeleton
                        active
                        paragraph={{ rows: 0 }}
                        title={{ width: 100 }}
                      />
                      <div style={{ marginTop: "8px" }}>
                        <Skeleton
                          active
                          paragraph={{ rows: 0 }}
                          title={{ width: 100 }}
                        />
                      </div>
                    </Col>
                  </Row>
                  <div style={{ marginTop: "1rem", textAlign: "center" }}>
                    <Skeleton.Button
                      active
                      size="default"
                      style={{ width: 100, height: 24 }}
                    />
                  </div>
                </PaymentSummary>

                <Divider />

                {/* Property Info Skeleton */}
                <PropertyInfo>
                  <div className="property-section">
                    <Skeleton
                      active
                      paragraph={{ rows: 0 }}
                      title={{ width: 150 }}
                      style={{ marginBottom: "1rem" }}
                    />
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="info-item"
                        style={{ marginBottom: "0.75rem" }}
                      >
                        <Skeleton
                          active
                          paragraph={{ rows: 0 }}
                          title={{ width: 60 }}
                          style={{ marginRight: "0.75rem" }}
                        />
                        <Skeleton
                          active
                          paragraph={{ rows: 0 }}
                          title={{ width: 150 }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="property-section">
                    <Skeleton
                      active
                      paragraph={{ rows: 0 }}
                      title={{ width: 150 }}
                      style={{ marginBottom: "1rem" }}
                    />
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="info-item"
                        style={{ marginBottom: "0.75rem" }}
                      >
                        <Skeleton
                          active
                          paragraph={{ rows: 0 }}
                          title={{ width: 60 }}
                          style={{ marginRight: "0.75rem" }}
                        />
                        <Skeleton
                          active
                          paragraph={{ rows: 0 }}
                          title={{ width: 150 }}
                        />
                      </div>
                    ))}
                  </div>
                </PropertyInfo>
              </PaymentCard>
            </Col>

            <Col xs={24} md={12}>
              <PaymentFormCard>
                <Skeleton
                  active
                  paragraph={{ rows: 0 }}
                  title={{ width: 180 }}
                  style={{ marginBottom: "1.5rem" }}
                />
                <Skeleton active paragraph={{ rows: 6 }} />
                <div style={{ marginTop: "1.5rem" }}>
                  <Skeleton.Button
                    active
                    size="large"
                    style={{ width: "100%", height: 40 }}
                  />
                </div>
              </PaymentFormCard>
            </Col>
          </Row>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Logo size="large" displayMode="both" color="black" linkTo={null} />
        <Text type="secondary">Secure Rent Payment Portal</Text>
        {testMode && (
          <Alert
            message="🧪 Test Mode Active"
            description="Payments are simulated and will not be processed for real."
            type="warning"
            showIcon
            style={{
              marginTop: "1rem",
              maxWidth: "400px",
              margin: "1rem auto 0 auto",
            }}
          />
        )}
      </Header>

      <Content>
        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} md={12}>
            <PaymentCard>
              <Title level={4}>
                <DollarOutlined style={{ marginRight: "8px" }} />
                Rent Payment
              </Title>

              <PaymentSummary>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="Amount Due"
                      value={displayAmountDue}
                      prefix="$"
                      valueStyle={{ color: "var(--color-primary)" }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Due Date"
                      value={formatDate(payment.dueDate)}
                      prefix={<CalendarOutlined />}
                    />
                  </Col>
                </Row>

                {/* Fee Breakdown */}
                {canAcceptPayments && !isAlreadyPaid && (
                  <>
                    {feeBreakdown ? (
                      <div
                        style={{
                          marginTop: "1rem",
                          padding: "1rem",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "0.85rem",
                            color: "#666",
                            marginBottom: "0.75rem",
                            fontStyle: "italic",
                          }}
                        >
                          Transaction fees are paid by the tenant
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: "1rem",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem",
                          }}
                        >
                          <span
                            style={{
                              textAlign: "left",
                              flex: "1 1 auto",
                              minWidth: 0,
                            }}
                          >
                            Rent Amount:
                          </span>
                          <span style={{ fontWeight: 500, flexShrink: 0 }}>
                            ${feeBreakdown.rentAmount.dollars}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: "1rem",
                            marginBottom: "0.5rem",
                            fontSize: "0.9rem",
                            color: "#666",
                          }}
                        >
                          <span
                            style={{
                              textAlign: "left",
                              flex: "1 1 auto",
                              minWidth: 0,
                            }}
                          >
                            {feeBreakdown.stripeFee.description}:
                          </span>
                          <span style={{ flexShrink: 0 }}>
                            ${feeBreakdown.stripeFee.dollars}
                          </span>
                        </div>
                        <div
                          style={{
                            borderTop: "2px solid #d0d0d0",
                            paddingTop: "0.5rem",
                            marginTop: "0.5rem",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: "1rem",
                            fontSize: "1rem",
                            fontWeight: 600,
                          }}
                        >
                          <span
                            style={{
                              textAlign: "left",
                              flex: "1 1 auto",
                              minWidth: 0,
                            }}
                          >
                            Total to Pay:
                          </span>
                          <span
                            style={{
                              color: "var(--color-primary)",
                              flexShrink: 0,
                            }}
                          >
                            ${feeBreakdown.totalAmount.dollars}
                          </span>
                        </div>
                      </div>
                    ) : feeLoading ? (
                      <div
                        style={{
                          marginTop: "1rem",
                          padding: "1rem",
                          textAlign: "center",
                          color: "#999",
                        }}
                      >
                        Calculating fees...
                      </div>
                    ) : (
                      <div
                        style={{
                          marginTop: "1rem",
                          padding: "0.75rem 1rem",
                          backgroundColor: "#f0f7ff",
                          borderRadius: "8px",
                          border: "1px solid #b3d9ff",
                          fontSize: "0.9rem",
                          color: "#0066cc",
                        }}
                      >
                        <strong>Note:</strong> Transaction fees are paid by the
                        tenant. Credit card payments include a 2.9% + $0.30
                        processing fee. ACH bank transfers include a 0.8%
                        processing fee (capped at $5.00). The total amount
                        charged will include these fees.
                      </div>
                    )}
                  </>
                )}

                <CenteredContent>
                  <PaymentStatusTag
                    color={getPaymentStatusColor(effectivePaymentStatus)}
                  >
                    {getPaymentStatusText(effectivePaymentStatus)}
                  </PaymentStatusTag>
                </CenteredContent>
              </PaymentSummary>

              {/* Payment Setup Warning */}
              {!canAcceptPayments && (
                <Alert
                  message="Payment Processing Not Available"
                  description={
                    <div>
                      <p>
                        {alertMessage ||
                          "Your landlord hasn't set up online payment processing yet."}
                      </p>
                      <p>
                        <strong>Please contact your landlord</strong> to let
                        them know they need to configure their payment system to
                        accept online rent payments.
                      </p>
                      <p>
                        In the meantime, you can pay using traditional methods
                        like check or cash.
                      </p>
                    </div>
                  }
                  type={alertType || "warning"}
                  showIcon
                  style={{ marginBottom: "1.5rem" }}
                />
              )}

              <Divider />

              <PropertyInfo>
                <div className="property-section">
                  <Title level={5}>
                    <HomeOutlined style={{ marginRight: "8px" }} />
                    Property Details
                  </Title>

                  {unit && (
                    <>
                      <div className="info-item">
                        <span className="info-label">Address:</span>
                        <span className="address-value">
                          {getUnitDisplayAddress(unit)
                            ? getUnitDisplayAddress(unit).replace(
                                /\s(\S+)$/,
                                "\u00A0$1"
                              )
                            : "Not provided"}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Unit:</span>
                        <span className="info-value">
                          {unit.unitNumber || "N/A"}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Rent:</span>
                        <span className="info-value">
                          ${payment?.amount || unit?.rent || "N/A"}/month
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {tenant && (
                  <div className="property-section">
                    <Title level={5}>
                      <UserOutlined style={{ marginRight: "8px" }} />
                      Tenant Information
                    </Title>
                    <div className="info-item">
                      <span className="info-label">Name:</span>
                      <span className="info-value">
                        {tenant.firstName && tenant.lastName
                          ? `${tenant.firstName} ${tenant.lastName}`
                          : tenant.name || tenant.displayName || "Not provided"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Email:</span>
                      <span className="info-value">{tenant.email}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Phone:</span>
                      <span className="info-value">
                        {tenant.phone
                          ? formatPhoneNumber(tenant.phone)
                          : "Not provided"}
                      </span>
                    </div>
                  </div>
                )}
              </PropertyInfo>
            </PaymentCard>
          </Col>

          <Col xs={24} md={12}>
            <PaymentFormCard>
              <Title level={4}>
                <CreditCardOutlined style={{ marginRight: "8px" }} />
                Payment Information
              </Title>

              {!canAcceptPayments ? (
                <Alert
                  message="Online Payment Unavailable"
                  description={
                    alertMessage ||
                    "Your landlord needs to set up their payment processing system before you can make online payments. Please contact them directly."
                  }
                  type={alertType || "info"}
                  showIcon
                  style={{ marginBottom: "1.5rem" }}
                />
              ) : isAlreadyPaid ? (
                <Alert
                  message="Payment Already Received"
                  description={
                    "Our records show this rent payment has been completed. If you believe this is an error, please contact your landlord or support."
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: "1.5rem" }}
                />
              ) : paymentStatus === "requires_verification" ? (
                // Bank Account Verification Screen
                <VerificationCard>
                  <VerificationIcon>
                    <BankOutlined />
                  </VerificationIcon>
                  <Title
                    level={3}
                    style={{ textAlign: "center", marginBottom: "1rem" }}
                  >
                    Verify your bank account
                  </Title>
                  <Paragraph
                    style={{
                      textAlign: "center",
                      fontSize: "16px",
                      color: "#666",
                      marginBottom: "1rem",
                    }}
                  >
                    Your bank requires verification via microdeposits. Stripe
                    will send two small deposits (less than $1 each) to your
                    bank account.
                  </Paragraph>
                  <Paragraph
                    style={{
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#999",
                      marginBottom: "2rem",
                      fontStyle: "italic",
                    }}
                  >
                    <strong>Important:</strong> The microdeposits typically
                    appear in your bank account within 1-2 business days. Check
                    your bank statement or online banking, then return to
                    Stripe's verification page to enter the deposit amounts.
                  </Paragraph>
                  <Button
                    type="primary"
                    size="large"
                    icon={
                      <LinkOutlined
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    }
                    onClick={() => {
                      if (verificationUrl) {
                        window.open(
                          verificationUrl,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }
                    }}
                    style={{
                      width: "100%",
                      height: "48px",
                      backgroundColor: "var(--color-primary)",
                      borderColor: "var(--color-primary)",
                      marginBottom: "1rem",
                    }}
                  >
                    Verify now
                  </Button>
                  <Text
                    type="secondary"
                    style={{
                      display: "block",
                      textAlign: "center",
                      fontSize: "14px",
                    }}
                  >
                    After you verify the microdeposits, your payment will
                    process automatically. ACH payments typically take 1-3
                    business days to complete.
                  </Text>
                  <Divider style={{ margin: "1.5rem 0" }} />
                  <Button
                    type="default"
                    size="large"
                    onClick={() => {
                      setPaymentStatus(null);
                      setVerificationUrl(null);
                      setPaymentIntentId(null);
                    }}
                    style={{ width: "100%" }}
                  >
                    Back to payment
                  </Button>
                </VerificationCard>
              ) : paymentStatus === "processing" ? (
                // ACH sent: payment is pending; show confirmation
                <ProcessingCard>
                  <SuccessIcon>
                    <CheckCircleOutlined />
                  </SuccessIcon>
                  <Title
                    level={3}
                    style={{ textAlign: "center", marginBottom: "1rem" }}
                  >
                    Payment sent through your bank
                  </Title>
                  <Paragraph
                    style={{
                      textAlign: "center",
                      fontSize: "16px",
                      color: "#666",
                      marginBottom: "2rem",
                    }}
                  >
                    Your payment has been submitted and is pending. ACH payments
                    typically take 1 to 3 business days. You&apos;ll receive a
                    confirmation email once it goes through.
                  </Paragraph>
                  <Paragraph
                    type="secondary"
                    style={{
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#888",
                      marginBottom: 0,
                    }}
                  >
                    It&apos;s okay to close this window.
                  </Paragraph>
                </ProcessingCard>
              ) : paymentStatus === "succeeded" ? (
                // Payment succeeded: show confirmation then refresh to show updated status
                <SuccessConfirmationCard>
                  <SuccessIcon>
                    <CheckCircleOutlined />
                  </SuccessIcon>
                  <Title
                    level={3}
                    style={{ textAlign: "center", marginBottom: "1rem" }}
                  >
                    Payment successful
                  </Title>
                  <Paragraph
                    style={{
                      textAlign: "center",
                      fontSize: "16px",
                      color: "#666",
                      marginBottom: "2rem",
                    }}
                  >
                    Your rent payment has been submitted successfully. You will
                    receive a confirmation email shortly.
                  </Paragraph>
                  <Button
                    type="primary"
                    size="large"
                    icon={
                      <CheckCircleOutlined
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    }
                    onClick={async () => {
                      await refetchPayment();
                      setPaymentStatus(null);
                    }}
                    style={{
                      width: "100%",
                      height: "48px",
                      backgroundColor: "var(--color-primary)",
                      borderColor: "var(--color-primary)",
                    }}
                  >
                    View updated status
                  </Button>
                </SuccessConfirmationCard>
              ) : isSubmittingPayment && !achClientSecret ? (
                // Card flow: show "Processing your payment" while waiting for backend
                <SubmittingCard>
                  <SubmittingSpinnerWrap>
                    <SubmittingSpinnerIcon />
                  </SubmittingSpinnerWrap>
                  <Title
                    level={4}
                    style={{ textAlign: "center", marginBottom: "0.5rem" }}
                  >
                    Processing your payment
                  </Title>
                  <Paragraph
                    type="secondary"
                    style={{ textAlign: "center", marginBottom: 0 }}
                  >
                    Please wait while we confirm your payment. Do not close this
                    page.
                  </Paragraph>
                </SubmittingCard>
              ) : achClientSecret || achEntryMode === "manual" ? (
                <PaymentFormCard style={{ position: "relative" }}>
                  {isSubmittingPayment && (
                    <SubmittingOverlay>
                      <SubmittingCard>
                        <SubmittingSpinnerWrap>
                          <SubmittingSpinnerIcon />
                        </SubmittingSpinnerWrap>
                        <Title
                          level={4}
                          style={{
                            textAlign: "center",
                            marginBottom: "0.5rem",
                          }}
                        >
                          Processing your payment
                        </Title>
                        <Paragraph
                          type="secondary"
                          style={{ textAlign: "center", marginBottom: 0 }}
                        >
                          Please wait while we confirm your payment. Do not
                          close this page.
                        </Paragraph>
                      </SubmittingCard>
                    </SubmittingOverlay>
                  )}
                  <BankConnectHeader>
                    <BankIconWrap>
                      <BankOutlined />
                    </BankIconWrap>
                    <div>
                      <Title level={5} style={{ margin: 0 }}>
                        {achEntryMode === "manual"
                          ? "Enter your bank details"
                          : "Connect your bank account"}
                      </Title>
                      <Paragraph
                        type="secondary"
                        style={{ margin: "4px 0 0", fontSize: "14px" }}
                      >
                        {achEntryMode === "manual"
                          ? "Use this if your bank is not supported by instant verification."
                          : "Verify securely through Stripe"}
                      </Paragraph>
                    </div>
                  </BankConnectHeader>
                  {achEntryMode === "manual" ? (
                    <ManualBankForm
                      rentPaymentId={id}
                      returnUrl={returnUrl}
                      totalAmountLabel={displayTotalWithFeeFormatted}
                      prefillName={undefined}
                      onSubmitting={() => setIsSubmittingPayment(true)}
                      onSuccess={({ status }) => {
                        setIsSubmittingPayment(false);
                        setAchEntryMode(null);
                        setPaymentStatus(
                          status === "succeeded" || status === "paid"
                            ? "succeeded"
                            : "processing"
                        );
                        if (status === "succeeded" || status === "paid") {
                          message.success("Payment successful.");
                        } else {
                          message.success(
                            "Payment submitted. You will receive a confirmation email."
                          );
                        }
                      }}
                      onError={(err) => {
                        setIsSubmittingPayment(false);
                        message.error(
                          err?.message || "Payment failed. Please try again."
                        );
                      }}
                      onBack={() => setAchEntryMode(null)}
                    />
                  ) : (
                    <>
                      <Elements
                        stripe={stripePromise}
                        options={{
                          clientSecret: achClientSecret,
                          appearance: { theme: "stripe" },
                        }}
                      >
                        <BankPaymentForm
                          clientSecret={achClientSecret}
                          returnUrl={returnUrl}
                          rentPaymentId={id}
                          prefillEmail={
                            emailForStripePrefill?.trim() || undefined
                          }
                          totalAmountLabel={displayTotalWithFeeFormatted}
                          onSubmitting={() => setIsSubmittingPayment(true)}
                          onSuccess={({ status }) => {
                            setIsSubmittingPayment(false);
                            setAchClientSecret(null);
                            setPaymentStatus(
                              status === "succeeded" || status === "paid"
                                ? "succeeded"
                                : "processing"
                            );
                            if (status === "succeeded" || status === "paid") {
                              message.success("Payment successful.");
                            } else {
                              message.success(
                                "Payment submitted. You will receive a confirmation email."
                              );
                            }
                          }}
                          onError={(err) => {
                            setIsSubmittingPayment(false);
                            message.error(
                              err?.message ||
                                "Payment failed. Please try again."
                            );
                          }}
                          onBack={() => {
                            setAchClientSecret(null);
                            setEmailForStripePrefill("");
                          }}
                        />
                      </Elements>
                      <Spacer $marginTop="1rem" />
                      <Paragraph
                        type="secondary"
                        style={{ fontSize: 13, marginBottom: 0 }}
                      >
                        <LinkOutlined style={{ marginRight: 6 }} />
                        <Typography.Link
                          type="secondary"
                          onClick={() => {
                            setAchClientSecret(null);
                            setAchEntryMode("manual");
                          }}
                        >
                          My bank isn&apos;t listed. Enter routing and account
                          number instead.
                        </Typography.Link>
                      </Paragraph>
                    </>
                  )}
                </PaymentFormCard>
              ) : (
                <StyledForm
                  form={form}
                  layout="vertical"
                  onFinish={handlePayment}
                  onFinishFailed={(errorInfo) => {
                    console.error(
                      "[TenantRentPayment] Form validation failed:",
                      errorInfo
                    );
                    message.error(
                      "Please fill in all required fields correctly."
                    );
                  }}
                  initialValues={{
                    paymentMethod: "bank",
                  }}
                >
                  <Form.Item
                    name="paymentMethod"
                    label="Payment Method"
                    rules={[
                      {
                        required: true,
                        message: "Please select a payment method",
                      },
                    ]}
                  >
                    <Select
                      onChange={(value) => {
                        setPaymentMethod(value);
                        setAchClientSecret(null);
                        form.setFieldsValue({ paymentMethod: value });
                      }}
                    >
                      <Option value="bank">
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <BankOutlined /> Bank Transfer (ACH)
                        </span>
                      </Option>
                      <Option value="card">
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <CreditCardOutlined /> Credit/Debit Card
                        </span>
                      </Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label="Email for Receipt"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input placeholder="your.email@example.com" />
                  </Form.Item>

                  {paymentMethod === "card" && (
                    <>
                      <Form.Item
                        label="Card Information"
                        required
                        help="Your card details are securely processed by Stripe. We never store your full card number."
                      >
                        <CardElementContainer>
                          <CardElement
                            options={{
                              style: {
                                base: {
                                  fontSize: "16px",
                                  color: "#424770",
                                  fontFamily:
                                    'var(--font-primary), -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  "::placeholder": {
                                    color: "#aab7c4",
                                  },
                                },
                                invalid: {
                                  color: "#9e2146",
                                },
                              },
                            }}
                          />
                        </CardElementContainer>
                      </Form.Item>

                      <Form.Item
                        name="cardholderName"
                        label="Cardholder Name"
                        rules={[
                          {
                            required: true,
                            message: "Please enter cardholder name",
                          },
                        ]}
                      >
                        <Input placeholder="John Doe" />
                      </Form.Item>
                    </>
                  )}

                  {paymentMethod === "bank" && (
                    <>
                      <StyledACHAlert
                        message="Pay with bank account"
                        description="Connect your bank through Stripe. You won't need to enter routing or account numbers. Stripe will open a secure flow to verify your account. ACH payments usually take 1-3 business days to process."
                        type="info"
                        showIcon
                      />
                      <Form.Item>
                        <ContinueBankButton
                          type="primary"
                          size="large"
                          icon={
                            <BankOutlined
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                          }
                          loading={loading}
                          disabled={isAlreadyPaid || !isValidReceiptEmail}
                          onClick={async () => {
                            if (isAlreadyPaid) {
                              message.warning(
                                "This payment has already been completed."
                              );
                              return;
                            }
                            if (!isValidReceiptEmail) {
                              message.warning(
                                "Please enter a valid email for your receipt first."
                              );
                              return;
                            }
                            setEmailForStripePrefill(receiptEmailTrimmed);
                            setLoading(true);
                            try {
                              const res = await createRentPaymentIntentACH({
                                rentPaymentId: id,
                              });
                              if (res?.client_secret) {
                                setAchClientSecret(res.client_secret);
                              } else {
                                throw new Error(
                                  res?.error || "Could not start bank payment"
                                );
                              }
                            } catch (e) {
                              const msg =
                                e?.message ||
                                "Failed to continue. Please try again.";
                              message.error(msg);
                              if (
                                msg.toLowerCase().includes("already") &&
                                msg.toLowerCase().includes("processed")
                              ) {
                                refetchPayment();
                              }
                            } finally {
                              setLoading(false);
                            }
                          }}
                          style={{ width: "100%", height: "48px" }}
                        >
                          {isMobile
                            ? `Pay $${displayTotalWithFeeFormatted} with bank account`
                            : `Continue to bank account - Pay $${displayTotalWithFeeFormatted}`}
                        </ContinueBankButton>
                      </Form.Item>
                    </>
                  )}

                  {paymentMethod === "card" && (
                    <Form.Item>
                      <PayButton
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        size="large"
                        disabled={
                          isAlreadyPaid ||
                          !stripe ||
                          !elements ||
                          !emailForReceipt ||
                          !String(emailForReceipt).trim()
                        }
                        style={{ width: "100%", height: "48px" }}
                        icon={
                          <CheckCircleOutlined
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        }
                      >
                        {`Pay $${displayTotalWithFeeFormatted}`}
                      </PayButton>
                    </Form.Item>
                  )}
                </StyledForm>
              )}

              {canAcceptPayments && (
                <SecurityNotice>
                  <SafetyOutlined
                    style={{
                      color: "var(--color-primary)",
                      marginRight: "8px",
                      fontSize: "1rem",
                    }}
                  />
                  <Text
                    type="secondary"
                    style={{ margin: 0, lineHeight: "1.5" }}
                  >
                    Your payment information is encrypted and secure. We use
                    industry-standard SSL encryption.
                  </Text>
                </SecurityNotice>
              )}
            </PaymentFormCard>
          </Col>
        </Row>
      </Content>
    </Container>
  );
};

// Wrapper component with Stripe Elements
const TenantRentPayment = () => {
  return (
    <Elements stripe={stripePromise}>
      <TenantRentPaymentInner />
    </Elements>
  );
};

export default TenantRentPayment;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 0;
`;

const Header = styled.div`
  background: white;
  padding: 2rem;
  text-align: center;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  @media (${device.mobile}) {
    padding: 1.5rem 1rem;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;

  @media (${device.mobile}) {
    padding: 2rem 1rem;
  }
`;

const PaymentCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  background: white;

  .ant-card-body {
    padding: 2rem;
  }

  @media (${device.mobile}) {
    .ant-card-body {
      padding: 1.5rem;
    }
  }
`;

const PaymentSummary = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;

  span {
    font-size: 1.2rem !important;
    font-weight: 600 !important;
  }

  /* Hide calendar icon on mobile - target the second column (Due Date) */
  @media (${device.mobile}) {
    .ant-row > .ant-col:nth-child(2) .ant-statistic-content-prefix {
      display: none;
    }
  }
`;

const NotFoundContent = styled.div`
  text-align: center;
  padding: 2rem 1rem;
`;

const NotFoundIcon = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
`;

const NotFoundActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
  margin-bottom: 1rem;
  width: 100%;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;

  .ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    line-height: 1.5;
    padding: 0 24px;
  }
`;

const HelpSection = styled.div`
  text-align: left;
  background: #f5f5f5;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
`;

const PropertyInfo = styled.div`
  margin-top: 1.5rem;

  .ant-typography {
    margin-bottom: 0.5rem;
  }

  .property-section {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  .info-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 0.75rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .info-label {
    font-weight: 600;
    color: #2c3e50;
    min-width: 80px;
    margin-right: 0.75rem;
    flex-shrink: 0;
  }

  .info-value {
    color: #5a6c7d;
    flex: 1;
    word-break: break-word;
    line-height: 1.5;
  }

  .address-value {
    color: #5a6c7d;
    flex: 1;
    line-height: 1.5;
    margin-top: 0.25rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
    text-align: center;
  }
`;

const PaymentFormCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  background: white;

  .ant-card-body {
    padding: 2rem;
  }

  @media (${device.mobile}) {
    .ant-card-body {
      padding: 1.5rem;
    }
  }
`;

const BankConnectHeader = styled.div`
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin: auto;
  margin-bottom: 1.5rem;

  @media (${device.mobile}) {
    margin-bottom: 1.25rem;
  }
`;

const BankIconWrap = styled.div`
  .anticon {
    font-size: 1.75rem;
    color: var(--color-primary, #10b981);
  }
`;

const BankFormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const PaymentElementWrapper = styled.div`
  min-height: 220px;
  margin-bottom: 8px;
  .StripeElement {
    width: 100%;
  }
  @media (${device.mobile}) {
    min-height: 200px;
  }
`;

const BankFormActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;

  .bank-pay-btn,
  .bank-back-btn {
    width: 100%;
    padding: initial;
  }

  .bank-back-btn span {
    color: black !important;
  }

  @media (${device.mobile}) {
    margin-top: 20px;
    padding-top: 16px;
  }
`;

const BackButton = styled(Button)`
  color: black;

  &&& {
    color: #262626 !important;
    border-color: #d9d9d9 !important;
    background: #fff !important;
  }
  &&&:hover:not(:disabled) {
    color: #262626 !important;
    border-color: var(--color-primary, #10b981) !important;
    background: #f6ffed !important;
  }
`;

const PayButton = styled(Button)`
  &&&.ant-btn-primary:disabled,
  &&&.ant-btn-primary[disabled] {
    background: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
    cursor: not-allowed;
    opacity: 1;
  }
`;

const ContinueBankButton = styled(Button)`
  &&&.ant-btn-primary:disabled,
  &&&.ant-btn-primary[disabled] {
    background: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
    cursor: not-allowed;
    opacity: 1;
  }
  &&&.ant-btn-primary:disabled .anticon,
  &&&.ant-btn-primary[disabled] .anticon {
    color: rgba(0, 0, 0, 0.25) !important;
  }
`;

const SecurityNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;

  .ant-typography {
    text-align: left;
  }

  @media (${device.mobile}) {
    flex-direction: column;
    justify-content: center;
    text-align: center;
    gap: 0.5rem;

    .ant-typography {
      text-align: center;
    }
  }
`;

const StyledACHAlert = styled(Alert)`
  margin-bottom: 2.5rem !important;

  @media (${device.mobile}) {
    margin-bottom: 3rem !important;
  }
`;

// Custom styles to fix visual inconsistencies
const StyledForm = styled(Form)`
  /* Default spacing for fields without help text */
  .ant-form-item {
    margin-bottom: 2.5rem !important;
  }

  /* Fields with help text get more spacing */
  .ant-form-item-with-help {
    margin-bottom: 4.5rem !important;
  }

  .ant-form-item-label {
    text-align: left;
    padding-bottom: 0.5rem;
  }

  .ant-form-item-explain {
    margin-top: 0.625rem;
    text-align: left;
  }

  /* Extra spacing for help text */
  .ant-form-item-explain-error,
  .ant-form-item-explain-success {
    margin-top: 0.625rem;
  }

  /* Additional spacing on mobile */
  @media (${device.mobile}) {
    /* Default spacing for fields without help text */
    .ant-form-item {
      margin-bottom: 2rem !important;
    }

    /* Fields with help text get more spacing on mobile */
    .ant-form-item-with-help {
      margin-bottom: 5rem !important;
    }

    .ant-form-item-label {
      padding-bottom: 0.75rem !important;
      margin-bottom: 0 !important;
    }

    .ant-form-item-explain {
      margin-top: 0.75rem !important;
    }

    /* Ensure help text has proper spacing on mobile */
    .ant-form-item-explain-error,
    .ant-form-item-explain-success {
      margin-top: 0.75rem !important;
    }
  }

  .ant-input {
    border-radius: 8px;
    border: 1px solid #d1d5db;
    padding: 0.75rem 1rem;
    font-size: 1rem;

    &:focus {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 2px rgba(44, 95, 45, 0.1);
    }

    &::placeholder {
      color: #9ca3af;
      opacity: 1;
    }
  }

  .ant-select {
    .ant-select-selector {
      border-radius: 8px;
      border: 1px solid #d1d5db;
      padding: 0.75rem 1rem;
      font-size: 1rem;
    }

    &.ant-select-focused .ant-select-selector {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 2px rgba(44, 95, 45, 0.1);
    }
  }

  .ant-btn {
    border-radius: 8px;
    height: 48px;
    font-size: 1.1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    .anticon {
      font-size: 1.2rem;
    }
  }
`;

const CardElementContainer = styled.div`
  border: 2px solid var(--color-border-light);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  background: white;
  transition: border-color 0.2s ease;
  margin: 0;

  &:focus-within {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(44, 95, 45, 0.1);
  }

  .StripeElement {
    padding: 0.5rem 0;
  }
`;

const VerificationCard = styled(Card)`
  text-align: center;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  .ant-card-body {
    padding: 2rem;
  }
`;

const VerificationIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(30, 58, 138, 0.1);
  color: #1e3a8a;
  margin: 0 auto 1.5rem auto;

  .anticon {
    font-size: 40px;
  }
`;

const ProcessingCard = styled(Card)`
  text-align: center;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  .ant-card-body {
    padding: 2rem;
  }
`;

const ProcessingIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
  margin: 0 auto 1.5rem auto;

  .anticon {
    font-size: 40px;
  }
`;

const SubmittingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 2rem;
`;

const SubmittingCard = styled.div`
  text-align: center;
  padding: 2rem;
  max-width: 360px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SubmittingSpinnerWrap = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const SubmittingSpinnerIcon = styled.span`
  display: inline-block;
  width: 48px;
  height: 48px;
  border: 4px solid rgba(44, 95, 45, 0.2);
  border-top-color: var(--color-primary, #2c5f2d);
  border-radius: 50%;
  animation: submitting-spin 0.8s linear infinite;
  @keyframes submitting-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SuccessConfirmationCard = styled(Card)`
  text-align: center;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  .ant-card-body {
    padding: 2rem;
  }
`;

const SuccessIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.15);
  color: var(--color-primary, #10b981);
  margin: 0 auto 1.5rem auto;

  .anticon {
    font-size: 40px;
  }
`;

const PaymentStatusTag = styled(Tag)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  margin: 1rem auto 0 auto;
  text-align: center;
  border: 1px solid;
  background-color: transparent;
  box-shadow: none;

  /* For processing/verification status - use app's primary green with light background */
  &.ant-tag-processing {
    background-color: rgba(134, 228, 157, 0.12) !important;
    border-color: rgba(134, 228, 157, 0.4) !important;
    color: #2c5f2d !important;

    .ant-tag-text {
      color: #2c5f2d !important;
    }

    .anticon {
      color: #5dd77a !important;
      margin-right: 0.375rem;
    }
  }

  /* For success status - use success green */
  &.ant-tag-success {
    background-color: rgba(16, 185, 129, 0.12) !important;
    border-color: rgba(16, 185, 129, 0.4) !important;
    color: #059669 !important;

    .ant-tag-text {
      color: #059669 !important;
    }

    .anticon {
      color: #10b981 !important;
      margin-right: 0.375rem;
    }
  }

  /* For error status - use error red with light background */
  &.ant-tag-error {
    background-color: rgba(239, 68, 68, 0.12) !important;
    border-color: rgba(239, 68, 68, 0.4) !important;
    color: #dc2626 !important;

    .ant-tag-text {
      color: #dc2626 !important;
    }

    .anticon {
      color: #ef4444 !important;
      margin-right: 0.375rem;
    }
  }

  /* For warning status - use warning amber with light background */
  &.ant-tag-warning {
    background-color: rgba(245, 158, 11, 0.12) !important;
    border-color: rgba(245, 158, 11, 0.4) !important;
    color: #d97706 !important;

    .ant-tag-text {
      color: #d97706 !important;
    }

    .anticon {
      color: #f59e0b !important;
      margin-right: 0.375rem;
    }
  }

  /* For default status - use subtle gray */
  &.ant-tag-default {
    background-color: #f1f5f9 !important;
    border-color: #cbd5e0 !important;
    color: #64748b !important;

    .ant-tag-text {
      color: #64748b !important;
    }

    .anticon {
      color: #94a3b8 !important;
      margin-right: 0.375rem;
    }
  }
`;

const CenteredContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;
